import LoadingSpinner from "@imminently/decisively-ui/LoadingSpinner";
import type { Elements } from "@/components/RuleGraph_FullRelease/types";
import type { MsgFromWorker, MsgToWorker } from "@/components/RuleGraph_FullRelease/worker/types";

import styled from "@emotion/styled";
import { DecisionGraph, isDecisionGraph } from "@imminently/decisively-core";
import { type FlowGraph, isFlowGraph as isFlowGraphObject } from "@/components/flow/DecisionFlowUtils";
import {
  type VisibleMask,
  limitedView,
  restrictedViewIds,
} from "@/components/GraphVisualisation/decisionGraphView";
import { isMac } from "@imminently/decisively-ui/Kbd";
import {
  Background,
  Controls,
  Handle,
  Position,
  ReactFlow,
  type Edge as ReactFlowEdge,
  type Node as ReactFlowNode,
  type ReactFlowProps,
  ReactFlowProvider,
  isNode,
  useEdgesState,
  useNodesState,
  useReactFlow,
  useStore,
} from "@xyflow/react";
import find from "lodash/find";
import React, { useEffect, useId, useMemo, useState } from "react";
import { useUpdateEffect } from "react-use";
import Icon from "@imminently/decisively-ui/Icon";
import { layout, layoutGraphBytes } from "@/lib/graphLayout";
// import { flushSync } from "react-dom"; // TODO ds just check if we need this anymore

export interface RestrictedView {
  rootPath: string;
  /** where null is no restriction/max */
  graphDepth: number | null;
  visibleNodes: string[] | null;
  nodesToMerge: string[] | null;
}

const ANIMATION_DURATION = 500;

const GraphRefit = (props: {
  refitKey?: string;
  onFit?: () => void;
  elementsState: ElementState;
  centerElementId?: string;
}) => {
  const { refitKey, onFit, elementsState, centerElementId } = props;
  const { setViewport, fitView, setCenter } = useReactFlow();

  const fitViewRef = React.useRef(fitView);
  fitViewRef.current = fitView;

  const refitKeyChanged = React.useRef<boolean>(false);
  const prevRefitKey = React.useRef<string | undefined>(undefined);
  if (prevRefitKey.current !== refitKey) {
    refitKeyChanged.current = true;
    prevRefitKey.current = refitKey;
  }

  const [forceRerender, setForceRerender] = useState<number>(0);

  useEffect(() => {
    if (!refitKeyChanged.current || elementsState.mode === "initializing" || elementsState.mode === "empty") {
      return;
    }
    refitKeyChanged.current = false;
    setTimeout(() => {
      if (centerElementId) {
        const element: any = find(elementsState.elements, ["id", centerElementId]);
        if (element) {
          const x = element.position.x + element.data.width / 2;
          const y = element.position.y + element.data.height / 2;
          // setCenter(x, y, 1, ANIMATION_DURATION);
          setCenter(x, y, { zoom: 1, duration: ANIMATION_DURATION });
          onFit?.();
          return;
        }
      }

      fitViewRef.current(undefined);
      onFit?.();
    }, 20);
  }, [refitKey, elementsState.mode]);

  return null;
};

const StyledControls = styled(Controls)`
  bottom: 10px;
  top: inherit;
`;

const SelectedSearchNodeClsnm = "selected-search-node";

const RelayoutBtnContainer = styled.div`
  position: absolute;
  bottom: 123px;
  left: 14px;
  z-index: 100;
  cursor: pointer;
  width: 28px;
  height: 28px;
  border: 1px solid #eee;
  display: flex;
  justify-content: center;
  align-items: center;
  align-content: center;
  padding-top: 4px;
  padding-left: 6px;
  background-color: white;

  svg {
    padding-top: 2px;
    padding-left: 2px;
    width: 20px;
    height: 20px;
  }
`;

const worker = (typeof Worker === "undefined" ? undefined : new Worker("/dagreWorker.js")) as Worker;

// a stable key per object (a decision graph, a view's mask), to tell when the graph to lay out has changed
const objectKeys = new WeakMap<object, number>();
let nextObjectKey = 0;
const objectKey = (value: object) => {
  let key = objectKeys.get(value);
  if (key === undefined) {
    key = ++nextObjectKey;
    objectKeys.set(value, key);
  }
  return key;
};

type ElementState =
  // TODO remove the union
  { mode: "initializing" | "empty"; elements: undefined } | { mode: "initialized" | "refresh"; elements: Elements };

export interface GraphViewNodeProps<N = any> {
  id: string;
  data: {
    height: number;
    width: number;
    node: N;
  };
  includeContextMenu?: boolean;
  xPos?: number;
  yPos?: number;
  isDragging?: boolean;
  isDecisionFlow?: boolean;
}

export interface GraphViewProps<N = any> {
  /**
   * A DecisionGraph (only the nodes it draws are written out for layout; any other form of graph is read as one), or
   * a decision flow (FlowGraph: UI boxes, laid out whole).
   */
  graph: DecisionGraph | FlowGraph | null | undefined;
  /** With a DecisionGraph: the ids in the view; null or undefined for every node. */
  visible?: VisibleMask;
  children?: React.ReactNode;
  refitKey?: string | number;
  recalculateKey?: string | number;
  nodeRenderLimit?: number;
  nodeComponent: React.ComponentType<GraphViewNodeProps<N>>;
  selectedNode?: string;
  showHidden?: boolean;
  onVisibleNodesChange?: (visibleNodes: string[] | null) => void;
  onRepositionNode?: (nodeId: string, x: number, y: number) => void;
  onNodeClick?: (nodeId: string, node: N) => void;
  onNodeCtrlClick?: (nodeId: string) => void;
  restrictedView?: RestrictedView;
  onRestrictedViewNodesMerge?: (nodes: string[]) => void;
  nodeHeight?: number;
  loading?: boolean;
  /** layout direction */
  rankDir?: "TB" | "BT" | "LR" | "RL";
  flowGraphProps?: ReactFlowProps;
  goal?: string;
}

const GraphView = <N,>(props: GraphViewProps<N>) => {
  const {
    graph,
    visible,
    goal,
    rankDir,
    loading,
    nodeHeight,
    children,
    restrictedView,
    onNodeClick,
    onNodeCtrlClick,
    onRepositionNode,
    showHidden,
    onVisibleNodesChange,
    selectedNode,
    refitKey,
    recalculateKey,
    nodeRenderLimit,
    nodeComponent,
    flowGraphProps,
  } = props;

  const id = useId();

  const resolvedNodeRenderLimit = nodeRenderLimit ?? 200;
  const [nodes, setNodes, onNodesChange] = useNodesState<any>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<any>([]);

  const [elementsState, setElementsState] = React.useState<ElementState>({
    mode: "empty",
    elements: undefined, // TODO might refactor this out into edge and node separately...not sure yet
  });
  const requiresRefit = React.useRef<boolean>(false);
  const requiresSelectedNodeReview = React.useRef<boolean>(false);

  const ignoreNewPositions = React.useRef<{
    repositionedNodes: { [key: string]: { x: number; y: number } };
    oldPositions: { [key: string]: { x: number; y: number } };
    // works nicely without these, but they might be useful in the future if you want even more control...
    // newNodesRootPos: { x: number, y: number };
    // newNodesRelativePosition: "above" | "below";
  } | null>(null);
  const resetLayout = React.useRef<boolean>(false);
  const additionalVisibleNodes = React.useRef<string[]>([]);

  // a decision flow is laid out here; any other graph is a DecisionGraph, laid out by the worker
  const isFlowGraph = isFlowGraphObject(graph);
  const decisionGraph = useMemo<DecisionGraph | null>(() => {
    if (!graph || isFlowGraphObject(graph)) {
      return null;
    }
    return isDecisionGraph(graph) ? graph : DecisionGraph.from(graph as any);
  }, [graph]);

  // Use refs to track previous values and prevent infinite loops
  const prevRawGraphRef = React.useRef<string | null>(null);
  const prevRestrictedViewRootPathRef = React.useRef<string | undefined>(undefined);

  // Create a stable key for the graph and view to detect actual changes
  const rawGraphKey = React.useMemo(() => {
    if (!decisionGraph) {
      return null;
    }
    return `decisionGraph:${objectKey(decisionGraph)}:${visible ? objectKey(visible) : "all"}`;
  }, [decisionGraph, visible]);

  /**
   * The worker's message carries DG bytes for only the nodes the view may draw (a restricted
   * view's neighbourhood, or as much of the view as fits the render limit), never the whole graph.
   */
  const layoutMessage = (
    restriction: MsgToWorker["restrictedView"] | undefined,
  ): Pick<MsgToWorker, "rawGraph" | "restrictedView"> & { limitedTo?: string[] } => {
    const dg = decisionGraph!;
    if (restriction?.rootPath) {
      const ids = restrictedViewIds(
        dg,
        visible,
        restriction.rootPath,
        restriction.graphDepth,
        restriction.additionalVisibleNodes,
      );
      return { rawGraph: layoutGraphBytes(dg, ids), restrictedView: restriction };
    }
    const view = limitedView(dg, visible, { limit: resolvedNodeRenderLimit, showHidden, goal });
    if (view.restriction) {
      console.log(`Render limited to ${resolvedNodeRenderLimit} nodes.`);
    }
    return {
      rawGraph: layoutGraphBytes(dg, view.ids),
      restrictedView: view.restriction,
      limitedTo: view.restriction?.additionalVisibleNodes,
    };
  };

  const repositionNode = (nodeId: string, x: number, y: number) => {
    if (ignoreNewPositions.current) {
      ignoreNewPositions.current.repositionedNodes = {
        ...(ignoreNewPositions.current.repositionedNodes || {}),
        [nodeId]: { x, y },
      };
    } else {
      ignoreNewPositions.current = {
        oldPositions: {},
        repositionedNodes: {
          [nodeId]: { x, y },
        },
      };
    }
  };

  // -- lifecycle

  React.useEffect(() => {
    if (elementsState.elements && elementsState.elements.length) {
      setNodes(elementsState.elements.filter((e) => e.type === "node") as unknown as any[]);
      setEdges(elementsState.elements.filter((e) => e.type !== "node") as unknown as any[]);
    }
  }, [elementsState]);

  const nodeTypes = React.useMemo(
    () => ({
      node: nodeComponent as any,
    }),
    [nodeComponent],
  );

  // -- web worker

  const workerListener = React.useCallback((e: MessageEvent<MsgFromWorker>) => {
    const { id: messageId, elements, visibleNodes } = e.data;

    // console.log("Worker message", e.data);

    if (messageId !== id) {
      return;
    }

    if (ignoreNewPositions.current) {
      console.log("Ignoring new positions");

      for (const newEl of elements) {
        if ((newEl as any).position) {
          const repositionData = ignoreNewPositions.current.repositionedNodes[newEl.id];
          const oldPosData = ignoreNewPositions.current.oldPositions[newEl.id];
          const replacementPosition = repositionData || oldPosData;
          if (replacementPosition) {
            newEl["position"] = { x: replacementPosition.x, y: replacementPosition.y };
          }
        }
      }
      // you don't need to hang onto ignoreNewPositions.current.repositionedNodes, because elements will get saved into
      // `elementsState` next, so this will find its way into ignoreNewPositions.current.oldPositions on the next expansion
      ignoreNewPositions.current = null;
      requiresRefit.current = false;
    }

    // const nodes = elements.filter(e => e.type === "node") as ReactFlowNode[];
    // const edges = elements.filter(e => e.type !== "node") as ReactFlowEdge[];

    for (const el of elements) {
      if (el.type === "node") {
        (el as ReactFlowNode).draggable = true;
      }
    }

    if (resetLayout.current) {
      setElementsState({ mode: "initialized", elements: [] });
      setTimeout(() => {
        setElementsState({ mode: "initialized", elements: elements });
      }, 1);
      resetLayout.current = false;
      ignoreNewPositions.current = null;
    } else {
      setElementsState({ mode: "initialized", elements: elements });
      onVisibleNodesChange?.(visibleNodes ?? null);
    }
    // console.log(`Number of (scoped) visible nodes on-screen is: ${visibleNodes?.length}`);

    if (requiresRefit.current) {
      setInternalRefitKey((prev) => prev + 1);
      requiresRefit.current = false;
    } else if (requiresSelectedNodeReview.current) {
      setInternalRefitKey((prev) => prev + 1);
      requiresSelectedNodeReview.current = false;
    }
  }, []);

  React.useEffect(() => {
    worker.addEventListener("message", workerListener);

    return () => {
      worker.removeEventListener("message", workerListener);
    };
  }, [workerListener]);

  // Process flow graphs directly without worker
  React.useEffect(() => {
    if (!isFlowGraph) {
      return;
    }
    const flowGraph = graph as FlowGraph;

    // Copy nodes - preserve dimensions so the layout spaces nodes correctly (min width/height for layout)
    const nodeData = new Map<string, any>();
    for (const [nodeId, data] of flowGraph.nodes) {
      nodeData.set(nodeId, {
        ...data,
        triggers: data.triggers ? data.triggers.map((t: any) => ({ ...t })) : data.triggers,
        width: Math.max(data.width ?? 220, 220),
        height: Math.max(data.height ?? 98, 98),
      });
    }

    // Layout the graph
    const placed = layout({
      rankdir: rankDir || "LR",
      nodesep: flowGraph.nodesep ?? 80,
      ranksep: flowGraph.ranksep ?? 120,
      nodes: [...nodeData].map(([nodeId, data]) => ({ id: nodeId, width: data.width, height: data.height })),
      edges: [...flowGraph.edges.values()],
    });

    // Convert to ReactFlow format
    const nodes = [...nodeData].map(([nodeId, data]) => {
      const width = data.width ?? 200;
      const height = data.height ?? 100;
      const centre = placed.nodes.get(nodeId);
      const x = centre?.x ?? 0;
      const y = centre?.y ?? 0;
      return {
        id: nodeId,
        type: "node",
        data: {
          label: data?.label ?? nodeId,
          node: data,
          value: data?.value ?? "",
          width,
          height,
          matchesSearch: false,
        },
        position: {
          x: x - width / 2,
          y: y - height / 2,
        },
        draggable: true,
      } as ReactFlowNode;
    });

    const points = new Map(placed.edges.map((e) => [`${e.v}\u0000${e.w}`, e.points]));
    const edges = [...flowGraph.edges.values()].map((edgeObj) => {
      const isBadEdge = Boolean(edgeObj.bad);
      let edgeLabel = (edgeObj.label ?? "") as string;
      // Ensure mapping node edges show labels: incoming "Uses", outgoing "Updates"
      if (!edgeLabel) {
        const sourceNode = flowGraph.nodes.get(edgeObj.v);
        const targetNode = flowGraph.nodes.get(edgeObj.w);
        if (targetNode?.type === "mapping" && edgeObj.targetHandle === "mapping") edgeLabel = "Uses";
        else if (sourceNode?.type === "mapping" && edgeObj.sourceHandle === "data") edgeLabel = "Updates";
        else if (
          sourceNode?.type === "trigger" &&
          targetNode?.type === "attribute" &&
          typeof edgeObj.sourceHandle === "string" &&
          edgeObj.sourceHandle.startsWith("task-")
        )
          edgeLabel = "Interview Goal";
      }
      return {
        id: `__${edgeObj.v}__${edgeObj.w}`,
        type: "smoothstep",
        source: edgeObj.v,
        target: edgeObj.w,
        sourceHandle: edgeObj.sourceHandle ?? undefined,
        targetHandle: edgeObj.targetHandle ?? undefined,
        animated: edgeObj.animated || false,
        label: edgeLabel,
        labelStyle: isBadEdge
          ? { fill: "var(--error-color, #d32f2f)", fontWeight: 600, fontSize: 11 }
          : edgeLabel
            ? { fontSize: 11 }
            : undefined,
        labelBgStyle: edgeLabel ? { fill: "var(--background, #fff)", fillOpacity: 0.9 } : undefined,
        points: points.get(`${edgeObj.v}\u0000${edgeObj.w}`) || [],
        style: {
          strokeWidth: isBadEdge ? 2 : (edgeObj as any).width || 1,
          stroke: isBadEdge ? "var(--error-color, #d32f2f)" : undefined,
        },
        data: {
          height: 0,
          width: 0,
          label: edgeLabel,
          node: {},
          value: "",
          matchesSearch: false,
        },
      } as ReactFlowEdge;
    });

    const elements: Elements = [...nodes, ...edges] as Elements;
    setElementsState({ mode: "initialized", elements });
    onVisibleNodesChange?.([...flowGraph.nodes.keys()]);
  }, [isFlowGraph, graph]);

  React.useEffect(() => {
    // don't use this path if flow graph
    if (isFlowGraph) {
      return;
    }
    // Skip if there's no graph to lay out yet
    if (!decisionGraph) {
      return;
    }

    // Check if dependencies actually changed to prevent infinite loops
    const rawGraphChanged = prevRawGraphRef.current !== rawGraphKey;
    const restrictedViewKey = recalculateKey?.toString();
    const restrictedViewChanged = prevRestrictedViewRootPathRef.current !== restrictedViewKey;
    if (restrictedViewChanged && selectedNode) {
      requiresSelectedNodeReview.current = true;
    }

    // If nothing actually changed, skip this effect
    if (!rawGraphChanged && !restrictedViewChanged && prevRawGraphRef.current !== null) {
      return;
    }

    // Update refs
    prevRawGraphRef.current = rawGraphKey;
    prevRestrictedViewRootPathRef.current = restrictedViewKey;

    // Regular graph processing via worker
    const msg: MsgToWorker = {
      id,
      rawGraph: new Uint8Array(),
      showHidden,
      nodeHeight,
      rankDir,
      restrictedView: restrictedView
        ? {
            rootPath: restrictedView.rootPath, // safe to force because it's tested in isGraphViewRestricted
            graphDepth: restrictedView.graphDepth ?? 1,
            additionalVisibleNodes: [],
          }
        : undefined,
    };

    const { limitedTo, ...layoutMsg } = layoutMessage(msg.restrictedView);
    Object.assign(msg, layoutMsg);
    additionalVisibleNodes.current = limitedTo ?? [];

    setElementsState((state) => {
      if (state.mode === "initializing" || state.mode === "refresh") {
        return state;
      }

      return { ...state, mode: state.mode === "initialized" ? "refresh" : "initializing" } as any;
    });

    console.log(`Showing graph ${restrictedView?.rootPath ? "with" : "without"} restrictions`);
    //console.log(msg);
    worker.postMessage(msg);
  }, [
    showHidden,
    rawGraphKey,
    nodeRenderLimit,
    recalculateKey,
    id,
    nodeHeight,
    rankDir,
    isFlowGraph,
  ]);

  const relayoutGraph = () => {
    if (elementsState.mode === "empty" || !decisionGraph) {
      return;
    }

    console.log("Re-layout graph");

    const msgToWorker: MsgToWorker = {
      id,
      rawGraph: new Uint8Array(),
      showHidden,
      nodeHeight,
      rankDir,
      restrictedView: restrictedView
        ? {
            rootPath: restrictedView.rootPath!, // safe to force because it's tested in isGraphViewRestricted
            graphDepth: restrictedView.graphDepth ?? 1,
            additionalVisibleNodes: [...(restrictedView.visibleNodes || [])],
          }
        : undefined,
    };
    const { limitedTo: _, ...layoutMsg } = layoutMessage(msgToWorker.restrictedView);
    Object.assign(msgToWorker, layoutMsg);

    // we might have some unfolds on an unrestricted graph that we need to preserve
    /*if (!restrictedView?.rootPath && additionalVisibleNodes.current.length && goalNodesOrdered?.[0]) {
      msgToWorker.restrictedView = {
        rootPath: goalNodesOrdered[0].path || goalNodesOrdered[0].id,
        graphDepth: 1,
        additionalVisibleNodes: additionalVisibleNodes.current,
      };
    }*/

    setElementsState(
      (state) =>
        ({
          ...state,
          mode: "refresh",
        }) as any,
    );
    requiresRefit.current = false;
    ignoreNewPositions.current = null;
    resetLayout.current = true;
    worker.postMessage(msgToWorker);
  };

  useUpdateEffect(() => {
    requiresRefit.current = true;
  }, [restrictedView?.rootPath]);

  React.useEffect(() => {
    // doing it this way goes back to the worker, and the whole graph gets re-layed out
    // we could append things to the existing elementsState.elements, but we'd have to work out ourselves where to put them
    // and it's nice to have the worker do the layout for us (even if we just tesselate the new nodes in the existing layout)

    if (restrictedView?.nodesToMerge?.length && restrictedView?.rootPath && decisionGraph) {
      console.log(
        `Showing ${(restrictedView?.nodesToMerge || []).length} additional nodes reachable from ${restrictedView?.rootPath || "(Limitation not specified)"}`,
      );

      additionalVisibleNodes.current = [...(restrictedView?.visibleNodes || []), ...restrictedView?.nodesToMerge];
      const msgToWorker: MsgToWorker = {
        id,
        rawGraph: new Uint8Array(),
        showHidden,
        nodeHeight,
        rankDir,
        restrictedView: restrictedView?.rootPath
          ? {
              rootPath: restrictedView?.rootPath,
              graphDepth: restrictedView?.graphDepth ?? 1,
              additionalVisibleNodes: additionalVisibleNodes.current,
            }
          : undefined,
      };
      const { limitedTo: _, ...layoutMsg } = layoutMessage(msgToWorker.restrictedView);
      Object.assign(msgToWorker, layoutMsg);
      restrictedView.nodesToMerge = null;
      // save old positions
      ignoreNewPositions.current = {
        repositionedNodes: ignoreNewPositions.current?.repositionedNodes || {},
        oldPositions: (elementsState.elements || []).reduce(
          (acc, el: any) => {
            if (el.position) {
              acc[el.id] = { x: el.position.x, y: el.position.y };
            }
            return acc;
          },
          {} as { [key: string]: { x: number; y: number } },
        ),
        // newNodes: restrictedViewNodesToMerge,
      };
      console.log("ignoreNewPositions.current", ignoreNewPositions.current);
      // seems counter intuitive, but we need to blank out the graph for a moment because whilst expanding
      // you can get weird artifacts like edges not drawing, despite the data being absolutely correct
      setElementsState(
        (state) =>
          ({
            ...state,
            mode: "refresh",
          }) as any,
      );
      worker.postMessage(msgToWorker);
    }
  }, [restrictedView?.nodesToMerge]);

  // -- rendering

  const renderRelayoutButton = () => {
    if (!relayoutGraph) {
      return null;
    }

    return (
      <RelayoutBtnContainer
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          relayoutGraph();
        }}
      >
        <Icon name="layout" />
      </RelayoutBtnContainer>
    );
  };

  const ignoreNextClick = React.useRef<boolean>(false);

  const handleNodeClick = React.useCallback<NonNullable<ReactFlowProps<any, any>["onNodeClick"]>>(
    (_, elementRaw) => {
      if (!isNode(elementRaw)) return;

      const targetId = (_?.target as any).id || "";
      const parentId = (_?.target as any)?.parentElement?.id || "";
      const parentParentId = (_?.target as any)?.parentElement?.parentElement?.id || "";
      const srcDomId = targetId || parentId || parentParentId;

      // catch any clicks that shouldn't have bubble up this far and drop them
      const isClickOnIgnoreEle = srcDomId.includes("graph_ignoreclick_");
      if (isClickOnIgnoreEle) {
        ignoreNextClick.current = true;
        return;
      }
      if (ignoreNextClick.current) {
        ignoreNextClick.current = false;
        return;
      }

      const element = elementRaw as ReactFlowNode<any>;
      const isModKeyPressed = isMac() ? _.metaKey : _.ctrlKey;

      if (isModKeyPressed && onNodeCtrlClick) {
        onNodeCtrlClick(element.data.node.path || element.data.node.id);
      } else {
        // @ts-ignore
        onNodeClick?.(element.data.node.path || element.data.node.id, element.data.node);
      }
    },
    [onNodeClick, onNodeCtrlClick],
  );

  const [internalRefitKey, setInternalRefitKey] = React.useState<number>(0);

  const [fitted, setFitted] = React.useState<boolean>(false);

  useUpdateEffect(() => {
    if (selectedNode) {
      setInternalRefitKey((prev) => prev + 1);
    }
  }, [selectedNode]);

  if (elementsState.mode === "empty") {
    return null;
  }

  // Only show "No flows to display" if we've finished processing and there are no nodes
  if (nodes.length === 0 && elementsState.mode === "initialized" && !loading && fitted) {
    return <div className="relative h-full grow">No flows to display</div>;
  }
  return (
    <div className={`relative h-full grow [&_.${SelectedSearchNodeClsnm}]:outline-[2px] [&_.${SelectedSearchNodeClsnm}]:outline-[indianred]`}>
      <ReactFlowProvider>
        <GraphRefit
          onFit={() => setFitted(true)}
          refitKey={`${refitKey}_${internalRefitKey}`}
          elementsState={elementsState}
          centerElementId={selectedNode}
        />
        {elementsState.elements ? (
          <ReactFlow<any, any>
            style={{ width: "100%" }}
            minZoom={0.01}
            nodes={nodes}
            edges={edges}
            nodeTypes={nodeTypes}
            onNodeClick={handleNodeClick}
            snapToGrid={false}
            onNodeDragStop={
              onRepositionNode
                ? (e, p) => {
                    if (p && p.position && p.id) {
                      repositionNode(p.id, p.position.x, p.position.y);
                    }
                  }
                : undefined
            }
            // nodesConnectable={false}
            nodesDraggable={true}
            draggable={true}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            {...flowGraphProps}
          >
            <StyledControls />
            {children}
            {renderRelayoutButton()}
          </ReactFlow>
        ) : null}
        {loading || !fitted || elementsState.mode === "refresh" || elementsState.mode === "initializing" ? (
          <div
            className="absolute inset-0 z-[100] flex items-center justify-center"
            style={{ background: `rgba(255,255,255,${!fitted ? 1 : 0.5})` }}
          >
            <LoadingSpinner size="sm"  />
          </div>
        ) : null}
      </ReactFlowProvider>
    </div>
  );
};

export default Object.assign(GraphView, {
  Background: Background,
  Handle: Handle,
  Position: Position,
  useStore: useStore,
});
